import { defineStore } from 'pinia';
import { collection, collectionGroup, doc, getDoc, getDocs, query, setDoc, where } from 'firebase/firestore';
import { clientSlugMatches, normalizeClientSlug, resolveClientSlug } from '~/lib/clientSlugs';
import { normalizeClientPortalRole, resolveClientPortalPermissions } from '~/lib/clientPortalPermissions';
import type { Client, ClientPortalInvitation, PortalAccessEntry } from '~/types/models';

type PortalClient = Client & {
    tenantId: string;
    __portalAccess?: PortalAccessEntry;
};

export const useClientPortalStore = defineStore('clientPortal', {
    state: () => ({
        currentTenantId: null as string | null,
        currentUserEmail: null as string | null,
        accessibleClients: [] as PortalClient[],
        currentClient: null as PortalClient | null,
        currentAccess: null as PortalAccessEntry | null,
        previewingAsClientId: null as string | null,
        loading: false,
        error: null as string | null,
    }),

    actions: {
        _portalAccessId(tenantId: string, clientId: string): string {
            return `${tenantId}|${clientId}`;
        },

        _normalizeEmail(value: unknown): string {
            return String(value || '').trim().toLowerCase();
        },

        _normalizeSlug(value: string): string {
            return normalizeClientSlug(value);
        },

        _hydratePortalClient(client: Client, clientId: string, tenantId: string, access?: PortalAccessEntry): PortalClient {
            return {
                ...client,
                id: clientId,
                tenantId,
                slug: resolveClientSlug(client),
                ...(access ? { __portalAccess: access } : {}),
            } as PortalClient;
        },

        _tenantIdFromPath(path: string): string | null {
            // collection group paths look like: tenants/<tenantId>/clients/<clientId>
            const parts = path.split('/');
            if (parts.length >= 2 && parts[0] === 'tenants') {
                return parts[1] || null;
            }
            return null;
        },

        _primaryClientEmail(client: PortalClient | null): string {
            if (!client) return '';

            const topLevelEmail = this._normalizeEmail(client.email);
            if (topLevelEmail) {
                return topLevelEmail;
            }

            const contacts = Array.isArray(client.contacts) ? client.contacts : [];
            const primaryContact = contacts.find((contact) => contact?.isPrimary === true) || contacts[0] || null;
            return this._normalizeEmail(primaryContact?.email);
        },

        _shouldRepairOwnerPortalAccess(client: PortalClient | null, access: PortalAccessEntry | null, userEmail: string | null): boolean {
            if (!client || !access) return false;

            const normalizedUserEmail = this._normalizeEmail(userEmail);
            if (!normalizedUserEmail) return false;

            if (normalizedUserEmail !== this._primaryClientEmail(client)) return false;

            return access.projectAccessMode !== 'all'
                || (Array.isArray(access.projectIds) && access.projectIds.length > 0)
                || access.portalRole !== 'client_owner'
                || resolveClientPortalPermissions(access).sendMessages !== true
                || resolveClientPortalPermissions(access).viewInvoices !== true
                || resolveClientPortalPermissions(access).viewQuotes !== true;
        },

        async _repairOwnerPortalAccess(client: PortalClient, access: PortalAccessEntry): Promise<PortalAccessEntry> {
            const { $db, $auth } = useNuxtApp();
            const userId = $auth.currentUser?.uid || null;
            if (!userId) {
                return access;
            }

            const repairedAccess: PortalAccessEntry = {
                ...access,
                projectAccessMode: 'all',
                projectIds: [],
                portalRole: 'client_owner',
                portalPermissions: resolveClientPortalPermissions({ isPrimary: true }),
            };

            try {
                await setDoc(
                    doc($db, `users/${userId}/portalAccess`, access.id || this._portalAccessId(client.tenantId, client.id)),
                    {
                        tenantId: client.tenantId,
                        clientId: client.id,
                        ...(access.contactId ? { contactId: access.contactId } : {}),
                        ...(access.contactName ? { contactName: access.contactName } : {}),
                        grantedAt: access.grantedAt || new Date(),
                        source: access.source || 'legacy-email-match',
                        projectAccessMode: 'all',
                        projectIds: [],
                        portalRole: 'client_owner',
                        portalPermissions: resolveClientPortalPermissions({ isPrimary: true }),
                        updatedAt: new Date(),
                    },
                    { merge: true },
                );
            } catch (error) {
                console.warn('Owner portal access repair could not be persisted; using in-memory scope for this session.', error);
            }

            return repairedAccess;
        },

        async _findClientsByEmailAndSlug(userEmail: string, clientSlug: string, tenantHint?: string | null): Promise<PortalClient[]> {
            const { $db } = useNuxtApp();
            const normalizedSlug = this._normalizeSlug(clientSlug);

            if (tenantHint) {
                const clientsRef = collection($db, `tenants/${tenantHint}/clients`);
                const tenantQuery = query(clientsRef, where('email', '==', userEmail), where('slug', '==', normalizedSlug));
                const tenantSnap = await getDocs(tenantQuery);
                if (!tenantSnap.empty) {
                    return tenantSnap.docs.map((clientDoc) => this._hydratePortalClient(clientDoc.data() as Client, clientDoc.id, tenantHint));
                }

                const emailQuery = query(clientsRef, where('email', '==', userEmail));
                const emailSnap = await getDocs(emailQuery);
                return emailSnap.docs
                    .filter((clientDoc) => clientSlugMatches(clientDoc.data() as Client, normalizedSlug))
                    .map((clientDoc) => this._hydratePortalClient(clientDoc.data() as Client, clientDoc.id, tenantHint));
            }

            const grouped = collectionGroup($db, 'clients');
            const groupedQuery = query(grouped, where('email', '==', userEmail), where('slug', '==', normalizedSlug));
            const groupedSnap = await getDocs(groupedQuery);

            if (!groupedSnap.empty) {
                return groupedSnap.docs
                    .map((clientDoc) => {
                        const tenantId = this._tenantIdFromPath(clientDoc.ref.path);
                        if (!tenantId) return null;
                        return this._hydratePortalClient(clientDoc.data() as Client, clientDoc.id, tenantId);
                    })
                    .filter((client): client is PortalClient => client !== null);
            }

            const groupedEmailQuery = query(grouped, where('email', '==', userEmail));
            const groupedEmailSnap = await getDocs(groupedEmailQuery);

            return groupedEmailSnap.docs
                .filter((clientDoc) => clientSlugMatches(clientDoc.data() as Client, normalizedSlug))
                .map((clientDoc) => {
                    const tenantId = this._tenantIdFromPath(clientDoc.ref.path);
                    if (!tenantId) return null;
                    return this._hydratePortalClient(clientDoc.data() as Client, clientDoc.id, tenantId);
                })
                .filter((client): client is PortalClient => client !== null);
        },

        async _getPortalAccessEntries(tenantHint?: string | null): Promise<PortalAccessEntry[]> {
            const { $db, $auth } = useNuxtApp();
            const userId = $auth.currentUser?.uid || null;
            if (!userId) {
                return [];
            }

            const accessRef = collection($db, `users/${userId}/portalAccess`);
            const accessQuery = tenantHint
                ? query(accessRef, where('tenantId', '==', tenantHint))
                : accessRef;
            const accessSnap = await getDocs(accessQuery);

            return accessSnap.docs.map((accessDoc) => {
                const data = accessDoc.data() as Omit<PortalAccessEntry, 'id'>;
                const isPrimaryLike = data.projectAccessMode !== 'selected';
                const portalRole = normalizeClientPortalRole(data.portalRole, isPrimaryLike);
                return {
                    id: accessDoc.id,
                    ...data,
                    portalRole,
                    portalPermissions: resolveClientPortalPermissions({
                        ...data,
                        portalRole,
                        isPrimary: isPrimaryLike,
                    }),
                };
            });
        },

        async _hydrateClientsFromPortalAccess(entries: PortalAccessEntry[], clientSlug?: string): Promise<PortalClient[]> {
            const normalizedSlug = clientSlug ? this._normalizeSlug(clientSlug) : null;
            const { $db, $auth } = useNuxtApp();
            const userEmail = this._normalizeEmail($auth.currentUser?.email);

            const clients = await Promise.all(entries.map(async (entry) => {
                const clientRef = doc($db, `tenants/${entry.tenantId}/clients`, entry.clientId);
                const clientSnap = await getDoc(clientRef);
                if (!clientSnap.exists()) {
                    return null;
                }

                const resolvedAccess: PortalAccessEntry = userEmail && userEmail === this._primaryClientEmail({
                        ...(clientSnap.data() as Client),
                        id: clientSnap.id,
                        tenantId: entry.tenantId,
                    } as PortalClient)
                        ? {
                            ...entry,
                            projectAccessMode: 'all',
                            projectIds: [],
                            portalRole: 'client_owner',
                            portalPermissions: resolveClientPortalPermissions({ isPrimary: true }),
                        }
                        : entry;
                const client = this._hydratePortalClient(clientSnap.data() as Client, clientSnap.id, entry.tenantId, resolvedAccess);

                if (normalizedSlug && !clientSlugMatches(clientSnap.data() as Client, normalizedSlug)) {
                    return null;
                }

                return client;
            }));

            return clients.filter((client): client is PortalClient => client !== null);
        },

        async _persistPortalAccessEntries(clients: PortalClient[], source: PortalAccessEntry['source']): Promise<void> {
            const { $db, $auth } = useNuxtApp();
            const userId = $auth.currentUser?.uid || null;
            if (!userId) {
                return;
            }

            await Promise.all(clients.map(async (client) => {
                try {
                    const userEmail = this._normalizeEmail($auth.currentUser?.email);
                    const isPrimaryContact = Boolean(userEmail && userEmail === this._primaryClientEmail(client));
                    const projectAccessMode = isPrimaryContact ? 'all' : client.__portalAccess?.projectAccessMode || 'all';
                    const portalRole = normalizeClientPortalRole(client.__portalAccess?.portalRole, isPrimaryContact || projectAccessMode !== 'selected');

                    await setDoc(
                        doc($db, `users/${userId}/portalAccess`, this._portalAccessId(client.tenantId, client.id)),
                        {
                            tenantId: client.tenantId,
                            clientId: client.id,
                            ...(client.__portalAccess?.contactId ? { contactId: client.__portalAccess.contactId } : {}),
                            ...(client.__portalAccess?.contactName ? { contactName: client.__portalAccess.contactName } : {}),
                            grantedAt: client.__portalAccess?.grantedAt || new Date(),
                            source,
                            projectAccessMode,
                            projectIds: isPrimaryContact ? [] : Array.isArray(client.__portalAccess?.projectIds) ? client.__portalAccess.projectIds : [],
                            portalRole,
                            portalPermissions: resolveClientPortalPermissions({
                                ...client.__portalAccess,
                                portalRole,
                                isPrimary: isPrimaryContact,
                            }),
                        },
                        { merge: true },
                    );
                } catch (error) {
                    console.warn('Portal access mapping could not be persisted; continuing with resolved portal access.', error);
                }
            }));
        },

        async _findClientsByAcceptedInvitation(clientSlug?: string, tenantHint?: string | null): Promise<PortalClient[]> {
            const { $db, $auth } = useNuxtApp();
            const userId = $auth.currentUser?.uid || null;
            const normalizedSlug = clientSlug ? this._normalizeSlug(clientSlug) : null;

            if (!userId) {
                return [];
            }

            const invitationQuery = query(
                collection($db, 'clientInvitations'),
                where('acceptedBy', '==', userId),
            );
            const invitationSnap = await getDocs(invitationQuery);

            const invitations = invitationSnap.docs
                .map((invitationDoc) => ({ id: invitationDoc.id, ...invitationDoc.data() } as ClientPortalInvitation))
                .filter((invitation) => invitation.status === 'accepted')
                .filter((invitation) => !invitation.revokedAt)
                .filter((invitation) => !tenantHint || invitation.tenantId === tenantHint);

            const clients = await Promise.all(invitations.map(async (invitation) => {
                const clientRef = doc($db, `tenants/${invitation.tenantId}/clients`, invitation.clientId);
                const clientSnap = await getDoc(clientRef);
                if (!clientSnap.exists()) {
                    return null;
                }

                const client = this._hydratePortalClient(clientSnap.data() as Client, clientSnap.id, invitation.tenantId);
                const isPrimaryContact = this._normalizeEmail(invitation.email) === this._primaryClientEmail(client);

                client.__portalAccess = {
                    id: this._portalAccessId(invitation.tenantId, invitation.clientId),
                    tenantId: invitation.tenantId,
                    clientId: invitation.clientId,
                    ...(invitation.contactId ? { contactId: invitation.contactId } : {}),
                    ...(invitation.contactName ? { contactName: invitation.contactName } : {}),
                    grantedAt: invitation.acceptedAt || invitation.invitedAt || new Date(),
                    source: 'invitation',
                    projectAccessMode: isPrimaryContact ? 'all' : invitation.projectAccessMode || 'all',
                    projectIds: isPrimaryContact ? [] : Array.isArray(invitation.projectIds) ? invitation.projectIds : [],
                    portalRole: normalizeClientPortalRole(invitation.portalRole, isPrimaryContact),
                    portalPermissions: resolveClientPortalPermissions({ ...invitation, isPrimary: isPrimaryContact }),
                };

                if (normalizedSlug && !clientSlugMatches(clientSnap.data() as Client, normalizedSlug)) {
                    return null;
                }

                return client;
            }));

            return clients.filter((client): client is PortalClient => client !== null);
        },

        async findFirstClientSlug(options?: { tenantHint?: string | null }): Promise<string | null> {
            const { $db, $auth } = useNuxtApp();
            const userEmail = $auth.currentUser?.email?.toLowerCase() || null;
            const tenantHint = options?.tenantHint || null;

            let directClients: PortalClient[] = [];
            try {
                const directEntries = await this._getPortalAccessEntries(tenantHint);
                directClients = await this._hydrateClientsFromPortalAccess(directEntries);
            } catch {
                directClients = [];
            }
            if (directClients[0]?.slug) {
                return resolveClientSlug(directClients[0]);
            }

            if (!userEmail) {
                return null;
            }

            if (tenantHint) {
                try {
                    const clientsRef = collection($db, `tenants/${tenantHint}/clients`);
                    const q = query(clientsRef, where('email', '==', userEmail));
                    const snap = await getDocs(q);
                    const matchedClients = snap.docs.map((clientDoc) => this._hydratePortalClient(clientDoc.data() as Client, clientDoc.id, tenantHint));
                    if (matchedClients.length > 0) {
                        await this._persistPortalAccessEntries(matchedClients, 'legacy-email-match');
                        return matchedClients[0] ? resolveClientSlug(matchedClients[0]) : null;
                    }
                } catch {
                    // Continue to invitation fallback.
                }

                try {
                    const invitedClients = await this._findClientsByAcceptedInvitation(undefined, tenantHint);
                    if (invitedClients.length > 0) {
                        await this._persistPortalAccessEntries(invitedClients, 'invitation');
                    }
                    return invitedClients[0] ? resolveClientSlug(invitedClients[0]) : null;
                } catch {
                    return null;
                }
            }

            try {
                const grouped = collectionGroup($db, 'clients');
                const q = query(grouped, where('email', '==', userEmail));
                const snap = await getDocs(q);
                const matchedClients = snap.docs
                    .map((clientDoc) => {
                        const tenantId = this._tenantIdFromPath(clientDoc.ref.path);
                        if (!tenantId) return null;
                        return this._hydratePortalClient(clientDoc.data() as Client, clientDoc.id, tenantId);
                    })
                    .filter((client): client is PortalClient => client !== null);
                if (matchedClients.length > 0) {
                    await this._persistPortalAccessEntries(matchedClients, 'legacy-email-match');
                    return matchedClients[0] ? resolveClientSlug(matchedClients[0]) : null;
                }
            } catch {
                // Continue to invitation fallback.
            }

            try {
                const invitedClients = await this._findClientsByAcceptedInvitation(undefined, tenantHint);
                if (invitedClients.length > 0) {
                    await this._persistPortalAccessEntries(invitedClients, 'invitation');
                }
                return invitedClients[0] ? resolveClientSlug(invitedClients[0]) : null;
            } catch {
                return null;
            }
        },

        clearAccess(): void {
            this.currentTenantId = null;
            this.currentUserEmail = null;
            this.accessibleClients = [];
            this.currentClient = null;
            this.currentAccess = null;
            this.previewingAsClientId = null;
            this.loading = false;
            this.error = null;
        },

        async initializeForClientSlug(clientSlug: string, options?: { previewClientId?: string | null, tenantHint?: string | null }): Promise<boolean> {
            const { $db, $auth } = useNuxtApp();
            const userEmail = $auth.currentUser?.email?.toLowerCase() || null;
            const previewClientId = options?.previewClientId || null;
            const tenantHint = options?.tenantHint || null;
            const normalizedSlug = this._normalizeSlug(clientSlug);

            if (!normalizedSlug) {
                this.clearAccess();
                this.error = 'Invalid client portal URL.';
                return false;
            }

            if (
                this.currentUserEmail === userEmail
                && this.previewingAsClientId === previewClientId
                && this.currentClient
                && this.currentClient.slug === normalizedSlug
                && (!tenantHint || this.currentTenantId === tenantHint)
            ) {
                return true;
            }

            this.loading = true;
            this.error = null;

            try {
                if (previewClientId) {
                    if (!tenantHint) {
                        this.error = 'Preview mode requires tenant context.';
                        this.accessibleClients = [];
                        this.currentClient = null;
                        return false;
                    }

                    const previewDocRef = doc($db, `tenants/${tenantHint}/clients`, previewClientId);
                    const previewSnap = await getDoc(previewDocRef);

                    if (!previewSnap.exists()) {
                        this.currentTenantId = tenantHint;
                        this.currentUserEmail = userEmail;
                        this.previewingAsClientId = previewClientId;
                        this.accessibleClients = [];
                        this.currentClient = null;
                        return false;
                    }

                    const previewClient = this._hydratePortalClient(previewSnap.data() as Client, previewSnap.id, tenantHint);

                    if (!clientSlugMatches(previewSnap.data() as Client, normalizedSlug)) {
                        this.currentTenantId = tenantHint;
                        this.currentUserEmail = userEmail;
                        this.previewingAsClientId = previewClientId;
                        this.accessibleClients = [];
                        this.currentClient = null;
                        return false;
                    }

                    this.currentTenantId = tenantHint;
                    this.currentUserEmail = userEmail;
                    this.previewingAsClientId = previewClientId;
                    this.accessibleClients = [previewClient];
                    this.currentClient = previewClient;
                    this.currentAccess = {
                        id: this._portalAccessId(tenantHint, previewClient.id),
                        tenantId: tenantHint,
                        clientId: previewClient.id,
                        grantedAt: new Date(),
                        source: 'legacy-email-match',
                        projectAccessMode: 'all',
                        projectIds: [],
                        portalRole: 'client_owner',
                        portalPermissions: resolveClientPortalPermissions({ isPrimary: true }),
                    };
                    return true;
                }

                let matches: PortalClient[] = [];

                const directEntries = await this._getPortalAccessEntries(tenantHint);
                matches = await this._hydrateClientsFromPortalAccess(directEntries, normalizedSlug);

                if (matches.length === 0 && userEmail) {
                    matches = await this._findClientsByEmailAndSlug(userEmail, normalizedSlug, tenantHint);
                    if (matches.length > 0) {
                        await this._persistPortalAccessEntries(matches, 'legacy-email-match');
                    }
                }

                if (matches.length === 0) {
                    matches = await this._findClientsByAcceptedInvitation(normalizedSlug, tenantHint);
                    if (matches.length > 0) {
                        await this._persistPortalAccessEntries(matches, 'invitation');
                    }
                }
                const chosen = matches[0] || null;

                this.currentTenantId = chosen?.tenantId || tenantHint || null;
                this.currentUserEmail = userEmail;
                this.previewingAsClientId = null;
                this.accessibleClients = matches;
                this.currentClient = chosen;
                let resolvedAccess = chosen
                    ? chosen.__portalAccess || directEntries.find((entry) => entry.tenantId === chosen.tenantId && entry.clientId === chosen.id) || {
                        id: this._portalAccessId(chosen.tenantId, chosen.id),
                        tenantId: chosen.tenantId,
                        clientId: chosen.id,
                        grantedAt: new Date(),
                        source: 'legacy-email-match',
                        projectAccessMode: 'all',
                        projectIds: [],
                        portalRole: 'client_owner',
                        portalPermissions: resolveClientPortalPermissions({ isPrimary: true }),
                    }
                    : null;

                if (this._shouldRepairOwnerPortalAccess(chosen, resolvedAccess, userEmail)) {
                    resolvedAccess = await this._repairOwnerPortalAccess(chosen as PortalClient, resolvedAccess as PortalAccessEntry);
                }
                this.currentAccess = resolvedAccess;

                return this.currentClient !== null;
            } catch (error) {
                this.error = `Error resolving portal access: ${error}`;
                console.error(this.error);
                this.accessibleClients = [];
                this.currentClient = null;
                return false;
            } finally {
                this.loading = false;
            }
        },
    },
});
